import React from 'react';

import { formatCurrency } from '../model/format';
import {
  baseCategories,
  disciplines,
  Discipline,
  managementCategory,
  supportCategory,
  useHGF2,
} from '../model/hgf2';
import { HGFCategory } from './hgf-category';

const yearOptions = Array.from({ length: 16 }, (_, index) => index); // 0..15

export function HGF2({
  location,
}: {
  location: { hash: string };
}): JSX.Element {
  const hgf = useHGF2(location.hash.substring(1));
  const breakdown = [
    `base salary of ${formatCurrency(hgf.base)}`,
    hgf.yearBonus > 0 && `year bonus of ${formatCurrency(hgf.yearBonus)}`,
    hgf.managementBonus > 0 &&
      `management bonus of ${formatCurrency(hgf.managementBonus)}`,
    hgf.supportBonus > 0 &&
      `supporting bonus of ${formatCurrency(hgf.supportBonus)}`,
  ]
    .filter((part): part is string => !!part)
    .join(' + ');
  return (
    <div className="container">
      <nav className="breadcrumb">
        <a href="/handbook">Handbook</a>
        <span>Human Growth Framework</span>
      </nav>
      <h2>nerdgeschoss Human Growth Framework</h2>
      <h4>Why do we need this?</h4>
      <p>
        The nerdgeschoss Human Growth Framework is designed to evaluate
        capabilities and achievements across employees while trying to fairly
        decide on salaries. We try to take the guesswork out of this nebulus
        topic while trying to eliminate discriminations like the gender pay gap.
      </p>
      <h4>How does it work?</h4>
      <p>
        Pick your job to see the matching Core Delivery track. The level you
        reach there decides your base salary, independent of whether you are a
        developer, designer or product manager. On top of that you can take on
        management and supporting responsibilities, each adding a bonus, and
        every year with us adds a little more.
      </p>
      <h4>Your Job</h4>
      <p>
        <select
          className="hgf__select"
          value={hgf.discipline}
          onChange={(event) =>
            hgf.setDiscipline(event.target.value as Discipline)
          }
        >
          {disciplines.map((discipline) => (
            <option key={discipline} value={discipline}>
              {discipline.charAt(0).toUpperCase() + discipline.slice(1)}
            </option>
          ))}
        </select>
      </p>
      <h4>Core Delivery</h4>
      <p>
        Your core track describes how you deliver work. Reach a level by
        regularly showing that you are capable of the mentioned examples or
        comparable achievements.
      </p>
      <HGFCategory
        category={baseCategories[hgf.discipline]}
        levels={hgf.levels.base}
        onChange={(track, level) => {
          hgf.changeRating('base', track, level);
        }}
      />
      <h4>Management Tracks</h4>
      <p>
        Taking on management responsibilities adds a bonus on top of your base
        salary (5% per level).
      </p>
      <HGFCategory
        category={managementCategory}
        levels={hgf.levels.management}
        onChange={(track, level) => {
          hgf.changeRating('management', track, level);
        }}
      />
      <h4>Supporting Tracks</h4>
      <p>
        Supporting tracks reward work that strengthens nerdgeschoss as a whole
        (5% per level).
      </p>
      <HGFCategory
        category={supportCategory}
        levels={hgf.levels.support}
        onChange={(track, level) => {
          hgf.changeRating('support', track, level);
        }}
      />
      <h4>Salary Calculation</h4>
      <p>
        Years with nerdgeschoss:{' '}
        <select
          className="hgf__select"
          value={hgf.years}
          onChange={(event) => hgf.setYears(parseInt(event.target.value))}
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </p>
      <p>
        {breakdown.charAt(0).toUpperCase() + breakdown.slice(1)} results in a
        total brut salary of
      </p>
      <div className="hgf__salary">
        {formatCurrency(hgf.total * 12)} per year
        <br />
        {formatCurrency(hgf.total)} per month
      </div>
    </div>
  );
}
